import type { ListingVersionItem } from '@/types/listing';

/**
 * The version history sidebar's grouping labels
 * (`design-input/listing-content-panel/prototype-extracted.md § Sidebar
 * grouping`).
 */
export type VersionGroupLabel =
  | 'Today'
  | 'Yesterday'
  | 'This week'
  | '1 week ago'
  | 'This month'
  | 'Earlier';

/**
 * `FR-20`'s grouping is elapsed days, not calendar days, and therefore has
 * no timezone at all (`components/product.md § Time on this screen`).
 * Both operands are absolute instants, so the browser's zone cannot change
 * the answer, and neither UTC midnight nor local midnight is a boundary
 * anywhere in this feature. The consequence, accepted there: a capture
 * from 11pm last night reads `Today` until 11pm tonight.
 */
export const relativeVersionGroup = (capturedAt: string): VersionGroupLabel => {
  const elapsedDays = Math.floor((Date.now() - Date.parse(capturedAt)) / 86_400_000);
  if (elapsedDays <= 0) return 'Today';
  if (elapsedDays === 1) return 'Yesterday';
  if (elapsedDays < 7) return 'This week';
  if (elapsedDays < 14) return '1 week ago';
  if (elapsedDays < 35) return 'This month';
  return 'Earlier';
};

export interface VersionGroup {
  label: VersionGroupLabel;
  versions: ListingVersionItem[];
}

/**
 * Walks the version list in order and starts a new group whenever the
 * label changes -- so a group can repeat if the list is not sorted
 * newest-first (`design-input/listing-content-panel/prototype-extracted.md
 * § Sidebar grouping`). The list is assumed already sorted (the backend
 * orders by `id DESC`, `components/product.md § The three routes`); this
 * function does not sort it.
 */
export const groupVersionsByElapsedTime = (versions: ListingVersionItem[]): VersionGroup[] => {
  const groups: VersionGroup[] = [];
  for (const version of versions) {
    const label = relativeVersionGroup(version.captured_at);
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.label === label) {
      lastGroup.versions.push(version);
    } else {
      groups.push({ label, versions: [version] });
    }
  }
  return groups;
};
